import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  createInstallLink,
  getInstallLink,
  markInstallLinkUsed,
} from "@/features/installations/services/installLinksService";
import type { CreateInstallLinkData } from "@/features/installations/services/installLinksService";

// ---------------------------------------------------------------------------
// Create install link mutation
// ---------------------------------------------------------------------------

export function useCreateInstallLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInstallLinkData) => {
      return createInstallLink(data);
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ["install-link", id] });
      toast.success("Link generado", {
        description: `Link temporal creado: install-${id}`,
      });
    },
    onError: (error: Error) => {
      toast.error("Error al generar link", {
        description: error.message || "No se pudo crear el link de descarga.",
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Get install link query
// ---------------------------------------------------------------------------

export function useGetInstallLink(id: string | undefined) {
  return useQuery({
    queryKey: ["install-link", id],
    queryFn: async () => {
      if (!id) return null;
      return getInstallLink(id);
    },
    enabled: !!id,
    staleTime: 0, // Always fresh — one-time use links
  });
}

// ---------------------------------------------------------------------------
// Mark install link used mutation
// ---------------------------------------------------------------------------

export function useMarkInstallLinkUsed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await markInstallLinkUsed(id);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["install-link", id] });
    },
  });
}
